import { useState, useEffect } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Palette, Upload, Loader2, Search, RefreshCw, 
  CheckCircle, FileSpreadsheet, Database
} from "lucide-react";

export function ColorLookupManager() {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [testCode, setTestCode] = useState("");
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setIsLoading(true);
    try {
      const response = await authFetch(`${API}/admin/color-lookup`, {
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Error loading color stats:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error("Le fichier doit être au format Excel (.xlsx)");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await authFetch(`${API}/admin/color-lookup/upload`, {
        method: "POST",
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        loadStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'upload");
      }
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  const handleTest = async () => {
    if (!testCode.trim()) {
      toast.error("Entrez un code à tester");
      return;
    }

    try {
      const response = await authFetch(`${API}/admin/color-lookup/${testCode.trim()}`, {
      });

      if (response.ok) {
        const data = await response.json();
        setTestResult(data);
      }
    } catch (error) {
      toast.error("Erreur lors du test");
    }
  };

  return (
    <div className="bg-white rounded-lg border border-zinc-200 p-6" data-testid="color-lookup-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Palette className="w-6 h-6 text-orange-500" />
          <div>
            <h2 className="text-xl font-semibold">Table des couleurs de fil</h2>
            <p className="text-sm text-zinc-500">Correspondance code référence → couleur</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadStats}
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Stats */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
        </div>
      ) : stats ? (
        <div className="space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-zinc-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Database className="w-5 h-5 text-zinc-500" />
                <span className="text-sm font-medium text-zinc-600">Codes en base</span>
              </div>
              <p className="text-2xl font-bold text-zinc-900">{stats.count}</p>
            </div>
            <div className="bg-zinc-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Palette className="w-5 h-5 text-zinc-500" />
                <span className="text-sm font-medium text-zinc-600">Couleurs</span>
              </div>
              <p className="text-2xl font-bold text-zinc-900">{stats.colors?.length || 0}</p>
            </div>
          </div>

          {/* Colors list */}
          {stats.colors?.length > 0 && (
            <div>
              <Label className="text-xs text-zinc-500 mb-2 block">Couleurs disponibles</Label>
              <div className="flex flex-wrap gap-2">
                {stats.colors.map(color => (
                  <span 
                    key={color} 
                    className="px-2 py-1 bg-zinc-100 text-zinc-700 text-sm rounded-full"
                  >
                    {color}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Upload */}
          <div className="border-t border-zinc-200 pt-4">
            <Label className="text-sm font-medium text-zinc-700 mb-2 block">
              Mettre à jour la table
            </Label>
            <div className="flex items-center gap-4">
              <label className="flex-1">
                <div className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${isUploading ? 'border-orange-300 bg-orange-50' : 'border-zinc-300 hover:border-orange-400 hover:bg-orange-50'}`}>
                  {isUploading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
                  ) : (
                    <Upload className="w-5 h-5 text-zinc-400" />
                  )}
                  <span className="text-sm text-zinc-600">
                    {isUploading ? "Upload en cours..." : "Cliquez pour uploader le fichier Excel"}
                  </span>
                </div>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={handleUpload}
                  disabled={isUploading}
                />
              </label>
            </div>
            <p className="text-xs text-zinc-400 mt-2">
              Le fichier doit contenir une colonne "COULEUR DU FIL" et des codes numériques
            </p>
          </div>

          {/* Test lookup */}
          <div className="border-t border-zinc-200 pt-4">
            <Label className="text-sm font-medium text-zinc-700 mb-2 block">
              Tester un code
            </Label>
            <div className="flex items-center gap-2">
              <Input
                value={testCode}
                onChange={(e) => setTestCode(e.target.value)}
                placeholder="Ex: 00084"
                className="w-40"
                onKeyDown={(e) => e.key === 'Enter' && handleTest()}
              />
              <Button onClick={handleTest} variant="outline" size="sm">
                <Search className="w-4 h-4 mr-1" />
                Rechercher
              </Button>
              {testResult && (
                <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${testResult.color ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                  {testResult.color ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-medium">{testResult.color}</span>
                    </>
                  ) : (
                    <span>Non trouvé</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-zinc-400">
          <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Aucune table de couleurs chargée</p>
          <p className="text-sm mt-1">Uploadez un fichier Excel pour commencer</p>
        </div>
      )}
    </div>
  );
}

export default ColorLookupManager;
